import React, { useState, useMemo, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ArrowLeft, Download, Search, Layers, Users, Zap } from 'lucide-react';
import { CSVLink } from "react-csv";
import Button from '../UI/Button';
import Card from '../UI/Card';
import Input from '../UI/Input';
import AgTableCard from '../UI/AgTableCard';
import InlineTooltip from '../UI/Tooltip/InlineTooltip';
import DateRangePicker from '../UI/DateRangePicker';
import { useUserContext } from '../../contexts/UserContext';
import { useContentfulContext } from '../../contexts/ContentfulContext';
import { getAnalyticsEventsData } from '../../utils/APIs';

const AnalyticsDashboard = () => {
    const { userData } = useUserContext();
    const { content } = useContentfulContext();
    const [searchParams, setSearchParams] = useSearchParams();

    const UI_CONTENT = content?.ANALYTICS_DASHBOARD_CONTENT || {
        start_date_label: "Start Date",
        end_date_label: "End Date",
        no_data_msg: "No usage data found for the selected date range.",
        no_data_available: "No data available.",
        back_btn_text: "Back to Dashboard",
        download_csv_btn: "Download CSV",
        search_placeholder: "Search...",
        total_records_text: "total records",
        view_details_btn: "View Details →",
        card_colors: ["#F5A623", "#4A90D9", "#7B61FF", "#E74C3C", "#2ECC71", "#9B59B6"]
    };

    const DASHBOARD_CARDS = content?.ANALYTICS_DASHBOARD_CONTENT?.dashboard_cards || [
        { label: "EXPERIENCES", width: "half", count_header: "Count", help_text: "Breakdown of experiences created, approved, and activated." },
        { label: "USER LOGINS", width: "half", count_header: "Total Logins", help_text: "Users with the most logins." },
        { label: "MOST USED FUNCTIONS", width: "full", count_header: "Executions", help_text: "The most frequently executed functions." }
    ];

    const [cardsData, setCardsData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [hasSubmitted, setHasSubmitted] = useState(false);
    const [gridApi, setGridApi] = useState(null);
    const [searchText, setSearchText] = useState('');
    const [allOrgs] = useState(false);

    const activeLabel = searchParams.get('label');
    const activeConfig = DASHBOARD_CARDS.find((e) => e.label === activeLabel);

    const activeCardData = cardsData.find((c) => c.label === activeLabel);
    const detailData = activeCardData?.data || [];

    const summaryKPIs = useMemo(() => {
        const expCard = cardsData.find((c) => c.label === "EXPERIENCES");
        const loginCard = cardsData.find((c) => c.label === "USER LOGINS");
        const funcCard = cardsData.find((c) => c.label === "MOST USED FUNCTIONS");

        const totalExperiences = (expCard?.data || []).reduce((acc, curr) => acc + (Number(curr.count || curr.created || 0)), 0);
        const totalLogins = (loginCard?.data || []).reduce((acc, curr) => acc + (Number(curr.count || 0)), 0);
        const totalExecutions = (funcCard?.data || []).reduce((acc, curr) => acc + (Number(curr.count || 0)), 0);

        return [
            {
                label: "Experiences Created",
                value: totalExperiences,
                icon: <Layers className="w-5 h-5 text-primary" />,
            },
            {
                label: "User Sessions",
                value: totalLogins,
                icon: <Users className="w-5 h-5 text-emerald-600" />,
            },
            {
                label: "Function Runs",
                value: totalExecutions,
                icon: <Zap className="w-5 h-5 text-indigo-600" />,
            },
        ];
    }, [cardsData]);

    const columns = useMemo(() => {
        const headers = UI_CONTENT.detail_table_headers || {};
        const cols = [
            { headerName: headers.index || '#', valueGetter: 'node.rowIndex + 1', width: 60, sortable: false, filter: false },
            { headerName: headers.name || 'Name', field: 'name', sortable: true, filter: true, resizable: true, flex: 1, minWidth: 200 }
        ];

        if (activeLabel === "EXPERIENCES") {
            cols.push(
                { headerName: headers.created || 'Created', field: 'created', sortable: true, filter: true, resizable: true, width: 120 },
                { headerName: headers.approved || 'Approved', field: 'approved', sortable: true, filter: true, resizable: true, width: 120 },
                { headerName: headers.activated || 'Activated', field: 'activated', sortable: true, filter: true, resizable: true, width: 120 }
            );
        } else {
            const activeCardConfig = DASHBOARD_CARDS.find(c => c.label === activeLabel);
            cols.push({ headerName: activeCardConfig?.count_header || headers.count || 'Count', field: 'count', sortable: true, filter: true, resizable: true, width: 150 });
        }

        return cols;
    }, [activeLabel, DASHBOARD_CARDS, UI_CONTENT.detail_table_headers]);

    const autoSizeStrategy = useMemo(() => ({
        type: window.innerWidth < 1024 ? 'fitCellContents' : 'fitGridWidth',
        defaultMinWidth: 100,
    }), []);

    // Set default date range to 30 days ago on mount
    useEffect(() => {
        const end = new Date();
        const start = new Date();
        start.setDate(end.getDate() - 30);
        
        const formattedStart = start.toISOString().split('T')[0];
        const formattedEnd = end.toISOString().split('T')[0];
        
        setStartDate(formattedStart);
        setEndDate(formattedEnd);
        
        if (userData?.token && userData?.selectedOrganization?.org_token) {
            handleSubmit(formattedStart, formattedEnd, allOrgs);
        }
    }, [userData?.token, userData?.selectedOrganization?.org_token]);

    const handleSubmit = async (fetchStart, fetchEnd, fetchAllOrgs = allOrgs) => {
        if (!fetchStart || !fetchEnd) return;

        if (activeLabel) {
            searchParams.delete('label');
            setSearchParams(searchParams);
        }

        setHasSubmitted(true);
        setLoading(true);
        setError('');

        try {
            const result = await getAnalyticsEventsData(
                userData?.token,
                userData?.selectedOrganization?.org_token,
                fetchStart,
                fetchEnd,
                fetchAllOrgs
            );
            setCardsData(result?.data || []);
        } catch (err) {
            console.error('Error fetching analytics:', err);
            setError(err?.message || 'Something went wrong. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleViewDetails = (label) => {
        searchParams.set('label', label);
        setSearchParams(searchParams);
        setSearchText('');
    };

    const handleBackToCards = () => {
        searchParams.delete('label');
        setSearchParams(searchParams);
        setSearchText('');
    };

    const handleSearch = (e) => {
        const val = e.target.value;
        setSearchText(val);
        gridApi?.setQuickFilter(val);
    };

    const onGridReady = (params) => {
        setGridApi(params.api);
    };

    return (
        <div className="w-full">
            {!activeLabel && (
                <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between mb-6 w-full bg-surface-subtle/80 p-3 sm:px-4 sm:py-2.5 rounded-xl border border-border-subtle shadow-xs">
                    <div className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-primary" />
                        <span className="text-xs font-semibold text-stride-heading tracking-wide">
                            Live Usage & Execution Metrics
                        </span>
                    </div>
                    <DateRangePicker 
                        startDate={startDate}
                        endDate={endDate}
                        onDateChange={(start, end) => {
                            setStartDate(start);
                            setEndDate(end);
                            handleSubmit(start, end, allOrgs);
                        }}
                        allowFuture={false}
                        hideLabel={true}
                    />
                </div>
            )}

            {/* Error Message */}
            {error && (
                <div className="p-4 mb-6 rounded-xl bg-danger-soft border border-danger/20 text-center text-sm font-medium text-danger">
                    {error}
                </div>
            )}

            {/* ========== DETAIL VIEW (AgGrid table) ========== */}
            {activeLabel && !loading && (
                <div className="w-full space-y-4">
                    {/* Top back action */}
                    <div>
                        <Button
                            variant="secondary"
                            size="sm"
                            onClick={handleBackToCards}
                            icon={<ArrowLeft size={15} />}
                            className="font-semibold"
                        >
                            {UI_CONTENT.back_btn_text}
                        </Button>
                    </div>
                    
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        <div>
                            <h1 className="text-xl md:text-2xl font-bold text-stride-heading m-0">
                                {activeConfig?.label || activeLabel}
                            </h1>
                            {activeConfig?.help_text && (
                                <p className="text-xs text-stride-muted mt-1 m-0">
                                    {activeConfig.help_text}
                                </p>
                            )}
                        </div>
                    </div>

                    <AgTableCard
                        header={
                            <div className="flex items-center justify-between gap-3 flex-wrap w-full">
                                {detailData.length > 0 ? (
                                    <Input
                                        prefixIcon={<Search size={15} />}
                                        value={searchText}
                                        onChange={handleSearch}
                                        placeholder={UI_CONTENT.search_placeholder}
                                        clearable
                                        className="w-full sm:w-64"
                                    />
                                ) : <div />}

                                {detailData.length > 0 && (
                                    <CSVLink 
                                        data={detailData} 
                                        filename={`platform-analytics-${(activeConfig?.label || activeLabel).replace(/\s+/g, '-').toLowerCase()}-${new Date().getTime()}.csv`}
                                    >
                                        <Button variant="secondary" size="sm" icon={<Download size={14} />} className="whitespace-nowrap font-semibold">
                                            {UI_CONTENT.download_csv_btn}
                                        </Button>
                                    </CSVLink>
                                )}
                            </div>
                        }
                        tableId="platform-analytics-detail-table"
                        loading={loading}
                        emptyMessage={UI_CONTENT.no_data_available}
                        rowData={detailData}
                        columnDefs={columns}
                        defaultColDef={{
                            sortable: true,
                            filter: true,
                            resizable: true,
                        }}
                        autoSizeStrategy={autoSizeStrategy}
                        onGridReady={onGridReady}
                    />
                </div>
            )}

            {/* ========== CARDS VIEW ========== */}
            {!activeLabel && (
                <>
                    {/* Loading skeleton */}
                    {loading && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 w-full">
                            {DASHBOARD_CARDS.map((config, idx) => {
                                const colSpanClass = config.width === 'full' ? 'md:col-span-2' : 'md:col-span-1';
                                return (
                                    <Card key={idx} variant="default" padding="none" className={`p-6 bg-white border border-border-subtle shadow-card animate-pulse ${colSpanClass}`}>
                                        <div className="h-5 bg-slate-100 rounded-md w-1/3 mb-5"></div>
                                        <div className="space-y-3">
                                            {Array.from({ length: 5 }).map((_, i) => (
                                                <div key={i} className="flex items-center gap-3">
                                                    <div className="h-4 bg-slate-100 rounded w-5"></div>
                                                    <div className="h-8 bg-slate-100 rounded-lg flex-1"></div>
                                                    <div className="h-4 bg-slate-100 rounded w-10"></div>
                                                </div>
                                            ))}
                                        </div>
                                    </Card>
                                );
                            })}
                        </div>
                    )}

                    {/* No data message */}
                    {!loading && !error && hasSubmitted && cardsData.length === 0 && (
                        <Card className="py-16 text-center max-w-lg mx-auto border border-dashed border-border">
                            <p className="text-sm text-stride-muted m-0">
                                {UI_CONTENT.no_data_msg}
                            </p>
                        </Card>
                    )}

                    {/* Top KPI Metrics Row (Grounded, non-hover cards matching XCM) */}
                    {!loading && !error && cardsData.length > 0 && (
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                            {summaryKPIs.map((kpi) => (
                                <div
                                    key={kpi.label}
                                    className="p-5 rounded-xl bg-white border border-border-subtle shadow-card flex items-center justify-between"
                                >
                                    <div>
                                        <span className="block text-xs font-semibold text-stride-muted uppercase tracking-wider mb-1">
                                            {kpi.label}
                                        </span>
                                        <strong className="block text-2xl md:text-3xl font-extrabold text-stride-heading tabular-nums">
                                            {kpi.value.toLocaleString()}
                                        </strong>
                                    </div>
                                    <div className="w-11 h-11 rounded-xl bg-primary-soft border border-primary/20 flex items-center justify-center shrink-0 shadow-xs">
                                        {kpi.icon}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Cards grid */}
                    {!loading && !error && cardsData.length > 0 && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 w-full">
                            {DASHBOARD_CARDS.map((config, cardIdx) => {
                                const card = cardsData.find((c) => c.label === config.label);
                                const items = card?.summary || card?.data || [];
                                const maxValue = items.length > 0 ? Math.max(...items.map((item) => item.count || 0)) : 0;
                                const bulletColor = UI_CONTENT.card_colors[cardIdx % UI_CONTENT.card_colors.length];
                                const colSpanClass = config.width === 'full' ? 'md:col-span-2' : 'md:col-span-1';

                                return (
                                    <Card
                                        key={config.label}
                                        variant="default"
                                        padding="none"
                                        className={`p-6 bg-white border border-border-subtle shadow-card ${colSpanClass}`}
                                    >
                                        <div className="flex items-center justify-between pb-3 mb-4 border-b border-border-subtle/80">
                                            <div className="flex items-center gap-2.5">
                                                <span className="w-2.5 h-2.5 rounded-full shrink-0 shadow-xs" style={{ backgroundColor: bulletColor }}></span>
                                                <div className="flex items-center gap-2">
                                                    <h3 className="text-xs md:text-sm font-bold text-stride-heading uppercase tracking-wider m-0">
                                                        {config.label}
                                                    </h3>
                                                    {config.help_text && <InlineTooltip content={config.help_text} />}
                                                </div>
                                                {items.length > 0 && (
                                                    <span className="ml-1 inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-semibold bg-primary-soft text-primary border border-primary/20">
                                                        {items.length.toLocaleString()} records
                                                    </span>
                                                )}
                                            </div>
                                            <Button
                                                variant="ghost"
                                                size="xs"
                                                onClick={() => handleViewDetails(config.label)}
                                                className="text-primary hover:text-primary-hover hover:bg-primary-soft font-semibold -mr-1"
                                            >
                                                {UI_CONTENT.view_details_btn}
                                            </Button>
                                        </div>

                                        {items.length === 0 ? (
                                            <div className="py-8 text-center text-sm text-stride-muted">
                                                {UI_CONTENT.no_data_available}
                                            </div>
                                        ) : (
                                            <div className="flex flex-col gap-2.5">
                                                {items.slice(0, 5).map((item, idx) => {
                                                    const value = item.count || 0;
                                                    const barWidth = maxValue > 0 ? Math.max((value / maxValue) * 100, 5) : 5;

                                                    return (
                                                        <div key={idx} className="flex items-center gap-2.5 p-1 -mx-1 rounded-lg hover:bg-slate-50/70 transition-colors text-xs md:text-sm">
                                                            <span className="w-6 h-6 rounded-md bg-primary-soft text-primary font-bold text-xs flex items-center justify-center shrink-0 border border-primary/10 shadow-xs font-mono">
                                                                {idx + 1}
                                                            </span>
                                                            <div className="flex-1 min-w-0 relative h-9 rounded-xl overflow-hidden flex items-center bg-white border border-border-subtle shadow-xs">
                                                                <div
                                                                    className="absolute inset-y-0 left-0 transition-all duration-300"
                                                                    style={{
                                                                        width: `${barWidth}%`,
                                                                        backgroundColor: `${bulletColor}20`,
                                                                        borderRight: `2px solid ${bulletColor}60`
                                                                    }}
                                                                />
                                                                <span className="relative z-10 truncate block px-3.5 font-semibold text-stride-heading text-xs" title={item.name}>
                                                                    {item.name}
                                                                </span>
                                                            </div>
                                                            <span className="px-2.5 py-1 rounded-md bg-surface-subtle border border-border-subtle text-stride-heading font-bold text-xs tabular-nums min-w-12 text-right shadow-xs">
                                                                {value.toLocaleString()}
                                                            </span>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </Card>
                                );
                            })}
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default AnalyticsDashboard;
